import {useState} from 'react'
import {Link, useNavigate} from 'react-router'
import {useMutation} from '@tanstack/react-query'
import {useAuth} from '../../auth/use-auth.js'
import {useDashboardLayout} from '../../hooks/use-dashboard-layout.js'
import {useInstallPrompt} from '../../hooks/use-install-prompt.js'
import {upgradeBackend} from '../../api/system.js'
import {useToastStore} from '../../state/toast.js'
import {Button} from '../../components/button/button.jsx'
import {Card} from '../../components/card/card.jsx'
import {Chip} from '../../components/chip/chip.jsx'
import {BottomSheet} from '../../components/bottom-sheet/bottom-sheet.jsx'
import {NotificationToggles} from './notification-toggles.jsx'
import styles from './profile.module.scss'

const LAYOUT_OPTIONS = [
    {value: 'climate-led', label: 'Climate'},
    {value: 'compact', label: 'Compact'},
    {value: 'ops-focused', label: 'Ops'}
]

const ADMIN_LINKS = [
    {to: '/admin/users', label: 'Users', hint: 'Issue activation links'},
    {to: '/admin/scenarios', label: 'Scenarios', hint: 'Edit automations'},
    {to: '/admin/tasks', label: 'Tasks', hint: 'Assign and track'},
    {to: '/admin/controls', label: 'Controls', hint: 'Hardware & commands'},
    {to: '/admin/readings', label: 'Sensor readings', hint: 'Devices & history'},
    {to: '/admin/audit', label: 'Audit log', hint: 'Forensic feed'}
]

export function Profile() {
    const navigate = useNavigate()
    const {user, logout} = useAuth()
    const {layout, setLayout} = useDashboardLayout()
    const {available: canInstall, promptInstall} = useInstallPrompt()
    const pushToast = useToastStore(s => s.pushToast)
    const [confirmLogout, setConfirmLogout] = useState(false)
    const [confirmUpgrade, setConfirmUpgrade] = useState(false)

    const upgradeMutation = useMutation({
        mutationFn: upgradeBackend,
        onSuccess: () => {
            setConfirmUpgrade(false)
            pushToast({title: 'Backend upgrade started', body: 'The API is pulling the latest revision and restarting.', variant: 'info'})
        }
        // errors are surfaced inline in the confirmation sheet via upgradeMutation.error
    })

    const handleLogout = async () => {
        await logout()
        navigate('/auth?invalid=1', {replace: true})
    }

    const closeUpgrade = () => {
        if (upgradeMutation.isPending)
            return
        setConfirmUpgrade(false)
        upgradeMutation.reset()
    }

    if (!user)
        return null

    const roleChips = Object.entries(user.roles || {}).map(([key, val]) => {
        if (key === 'admin' && val === true)
            return {key, label: 'Admin', variant: 'lime'}
        if (typeof val === 'string')
            return {key, label: `${key}: ${val}`, variant: 'sage'}
        return {key, label: key, variant: 'sage'}
    })

    return (
        <div className={styles.screen}>
            <header className={styles.header}>
                <p className={styles.eyebrow}>Signed in</p>
                <h1 className={styles.title}>{user.username}</h1>
                {user.email && <p className={styles.email}>{user.email}</p>}
                <div className={styles.chips}>
                    {roleChips.map(rc => <Chip key={rc.key} variant={rc.variant}>{rc.label}</Chip>)}
                </div>
            </header>

            <Card title="Dashboard layout">
                <div className={styles.radioGroup} role="radiogroup" aria-label="Dashboard layout">
                    {LAYOUT_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={layout === opt.value}
                            className={layout === opt.value ? styles.radioActive : styles.radio}
                            onClick={() => setLayout(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </Card>

            <Card title="Notifications">
                <NotificationToggles/>
            </Card>

            {user.roles?.admin && (
                <Card title="Admin">
                    <div className={styles.adminLinks}>
                        {ADMIN_LINKS.map(link => (
                            <Link key={link.to} to={link.to} className={styles.adminLink}>
                                <span className={styles.adminLabel}>{link.label}</span>
                                <span className={styles.adminHint}>{link.hint}</span>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {user.roles?.admin && (
                <Card title="Backend">
                    <p className={styles.coming}>
                        Pull the latest API revision and restart the backend process. The service is briefly
                        unavailable while it restarts.
                    </p>
                    <Button variant="secondary" onClick={() => setConfirmUpgrade(true)}>Upgrade backend</Button>
                </Card>
            )}

            <Card title="Report an issue">
                <p className={styles.coming}>
                    Found something off in the greenhouse? File it with a photo and a supervisor will triage.
                </p>
                <Button variant="secondary" onClick={() => navigate('/issues/new')}>Report an issue</Button>
            </Card>

            {canInstall && (
                <Card title="Install VerdaSync">
                    <p className={styles.coming}>Add VerdaSync to your home screen for one-tap launch.</p>
                    <Button variant="secondary" onClick={promptInstall}>Install app</Button>
                </Card>
            )}

            <div className={styles.logout}>
                <Button variant="secondary" onClick={() => setConfirmLogout(true)}>Sign out</Button>
            </div>

            <BottomSheet open={confirmLogout} onClose={() => setConfirmLogout(false)} title="Sign out?">
                <p className={styles.confirmBody}>
                    You'll need your activation link or credentials to sign back in.
                </p>
                <div className={styles.confirmActions}>
                    <Button variant="ghost" onClick={() => setConfirmLogout(false)}>Cancel</Button>
                    <Button onClick={handleLogout}>Sign out</Button>
                </div>
            </BottomSheet>

            <BottomSheet open={confirmUpgrade} onClose={closeUpgrade} title="Upgrade backend?">
                <p className={styles.confirmBody}>
                    This discards local changes on the server, pulls the latest revision, and restarts the API.
                    The backend will be briefly unavailable.
                </p>
                {upgradeMutation.isError && (
                    <pre className={styles.upgradeError} role="alert">
                        {upgradeMutation.error?.message || 'Upgrade failed.'}
                    </pre>
                )}
                <div className={styles.confirmActions}>
                    <Button variant="ghost" onClick={closeUpgrade} disabled={upgradeMutation.isPending}>Cancel</Button>
                    <Button onClick={() => upgradeMutation.mutate()} loading={upgradeMutation.isPending}>
                        Upgrade backend
                    </Button>
                </div>
            </BottomSheet>
        </div>
    )
}
